/* eslint-disable react/jsx-props-no-spreading */
import {
  Button,
  FormControl,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';

import { InviteAcceptanceCredentials } from '../types/preauth/Preauth';
import { Locked } from '@/client/components/icons/ContinuIcons';
import PreAuthService from '@/client/services/api/PreAuthService';
import { useAuthInviteSetup } from '../services/hooks/preauth/useAuthInviteSetup';
import { useConfigStore } from '@/client/services/state/configStore';
import useDocumentTitle from '../utils/useDocumentTitle';
import { useLogin } from '@/client/services/hooks/preauth/useLogin';
import { useMutation } from '@tanstack/react-query';
import { useHandoffParams } from '../services/hooks/preauth/useHandoffParams';
import { usePartnerStore } from '../services/state/partnerStore';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

interface Inputs extends InviteAcceptanceCredentials {
  confirmPassword: string;
}

export default function AcceptInvite() {
  useDocumentTitle('Accept Invite');
  const { config } = useConfigStore();
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { login } = useLogin();
  const domain = config.host;
  const searchParams = useHandoffParams();
  const inviteKey = searchParams.get('key') ?? '';
  const inviteEmail = searchParams.get('email') ?? '';
  const { setPartner, clearPartnerStore } = usePartnerStore();
  const { isLoading, isError, data, error } = useAuthInviteSetup(inviteKey, inviteEmail);

  useEffect(() => {
    if (data?.partner) {
      setPartner(data?.partner);
    } else {
      clearPartnerStore();
    }
  }, [data?.partner]);

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<Inputs>({
    defaultValues: {
      firstName: searchParams.get('fname') || '',
      lastName: searchParams.get('lname') || '',
      key: inviteKey,
      email: inviteEmail,
      domain,
    },
  });
  const [mismatch, setMismatch] = useState<boolean>(false);
  const createPasswordPlaceholder = t('global.forms.labels_passwordNew');
  const confirmPasswordPlaceholder = t('global.forms.labels_passwordConfirm');
  const firstNamePlaceholder = t('global.forms.labels_firstName');
  const lastNamePlaceholder = t('global.forms.labels_lastName');

  const acceptInvite = useMutation({
    mutationFn: ({
      firstName,
      lastName,
      domain: mutationDomain,
      key,
      email,
      password,
    }: InviteAcceptanceCredentials) =>
      PreAuthService.acceptInvite({
        firstName,
        lastName,
        domain: mutationDomain,
        key,
        email,
        password,
      }),
    onSuccess: (res, { email, password }) => {
      // TODO: check `validateAndUseAuthtoken`
      // TODO: Application.analyticsProvider.trackEvent('signin');
      const purchaseProductId = searchParams.get('purchaseproductid') || undefined;
      login({ userid: email, password, loginType: 'email', purchaseProductId });
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('preauth.invite.errorConfirmingInvitation'),
      }),
  });

  const onSubmit: SubmitHandler<Inputs> = (submitData) => {
    acceptInvite.mutate(submitData);
  };

  if (isLoading || !inviteKey || !inviteEmail) {
    return (
      <Text fontSize="14px" fontWeight={500} color="neutral.800">
        Please wait... if this screen persists, please request a new invitation.
      </Text>
    );
  }

  if (isError) {
    return (
      <Text fontSize="14px" fontWeight={500} color="neutral.800">
        Error: {error.message}
      </Text>
    );
  }

  return (
    <Stack spacing={4} width="100%">
      <Text fontSize={{ base: '24px', md: '36px' }} fontWeight={700}>
        {t('preauth.invite.completeInvite')}
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4}>
          <FormControl isInvalid={!!errors.firstName}>
            <Input
              id="firstName"
              type="text"
              placeholder={firstNamePlaceholder}
              variant="neutral"
              {...register('firstName', { required: true })}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.lastName}>
            <Input
              id="lastName"
              type="text"
              placeholder={lastNamePlaceholder}
              variant="neutral"
              {...register('lastName', { required: true })}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.password}>
            <InputGroup>
              <InputLeftElement>
                <Locked color="brand.gray.500" />
              </InputLeftElement>

              <Input
                id="password"
                type="password"
                placeholder={createPasswordPlaceholder}
                variant="neutral"
                {...register('password', { required: true })}
              />
            </InputGroup>

            <Text fontSize="14px" fontWeight={500} color="error">
              {errors.password && 'Password is required'}
            </Text>
          </FormControl>

          <FormControl isInvalid={!!errors.confirmPassword}>
            <InputGroup>
              <InputLeftElement>
                <Locked color="brand.gray.500" />
              </InputLeftElement>

              <Input
                id="confirmPassword"
                type="password"
                placeholder={confirmPasswordPlaceholder}
                variant="neutral"
                {...register('confirmPassword', {
                  required: true,
                  validate: (value) => {
                    if (watch('password') !== value) {
                      setMismatch(true);
                      return '';
                    }

                    setMismatch(false);
                  },
                })}
              />
            </InputGroup>

            <Text fontSize="14px" fontWeight={500} color="error">
              {errors.password && 'Password confirmation is required'}
            </Text>

            {mismatch && (
              <Text fontSize="14px" fontWeight={500} color="error">
                {t('preauth.acceptinvite.passwords_dont_match')}
              </Text>
            )}
          </FormControl>

          <Button type="submit" isDisabled={!isValid} isLoading={isSubmitting}>
            {t('preauth.invite.confirmInvitation')}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
}
